/**
 * Design tokens — spacing, typography, shadows, and semantic mappings.
 * Color hex values live in colors.ts only.
 */

import { adminColors as c } from "./colors";

export { adminColors } from "./colors";
export type { AdminColors } from "./colors";

export const THEME_MODES = ["light"] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export const BREAKPOINTS = {
	xs: 0,
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280,
	"2xl": 1536,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/** Expanded sidebar width — matches institute / employer portals. */
export const SIDEBAR_EXPANDED_WIDTH_PX = 228;
export const SIDEBAR_COLLAPSED_WIDTH_PX = 80;

const shared = {
	typography: {
		fontFamily: {
			sans: '"Poppins", sans-serif',
			mono: "ui-monospace, 'Cascadia Code', 'Segoe UI Mono', monospace",
		},
		fontSize: {
			xs: "0.75rem",
			sm: "0.875rem",
			base: "1rem",
			lg: "1.125rem",
			xl: "1.25rem",
			"2xl": "1.5rem",
			"3xl": "1.875rem",
			"4xl": "2.25rem",
		},
		fontWeight: {
			normal: 400,
			medium: 500,
			semibold: 600,
			bold: 700,
		},
		lineHeight: {
			tight: 1.25,
			normal: 1.5,
			relaxed: 1.625,
		},
	},
	spacing: {
		0: "0",
		1: "0.25rem",
		2: "0.5rem",
		3: "0.75rem",
		4: "1rem",
		5: "1.25rem",
		6: "1.5rem",
		8: "2rem",
		10: "2.5rem",
		12: "3rem",
		16: "4rem",
	},
	radius: {
		none: "0",
		sm: "0.375rem",
		md: "0.5rem",
		lg: "0.75rem",
		xl: "1rem",
		full: "9999px",
	},
	shadow: {
		sm: "0 1px 2px 0 rgb(15 23 42 / 0.05)",
		card: "0 4px 12px 0 rgb(0 0 0 / 0.05)",
		md: "0 4px 6px -1px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.08)",
		lg: "0 10px 15px -3px rgb(15 23 42 / 0.08), 0 4px 6px -4px rgb(15 23 42 / 0.08)",
	},
	zIndex: {
		base: 0,
		dropdown: 20,
		sidebar: 30,
		header: 40,
		modal: 50,
		snackbar: 60,
		tooltip: 70,
	},
	motion: {
		durationFast: "150ms",
		durationNormal: "250ms",
		durationSlow: "350ms",
		easingDefault: "cubic-bezier(0.4, 0, 0.2, 1)",
	},
	layout: {
		sidebarWidth: `${SIDEBAR_EXPANDED_WIDTH_PX}px`,
		sidebarCollapsedWidth: `${SIDEBAR_COLLAPSED_WIDTH_PX}px`,
		headerHeight: "3.5rem",
		footerHeight: "3.25rem",
		containerSm: "48rem",
		containerMd: "64rem",
		containerLg: "80rem",
	},
} as const;

/** @deprecated Prefer adminColors.brand — kept for existing imports */
export const brandPrimary = c.brand.primary;
export const brandPrimaryHover = c.brand.primaryHover;
export const brandPrimaryDark = c.brand.primaryDark;
export const brandPrimaryMuted = c.brand.primaryMuted;
export const brandPrimaryMutedBg = c.brand.primaryMutedBg;
export const brandPrimarySurface = c.brand.surface;
export const brandPrimarySurfaceHover = c.brand.surfaceHover;
export const brandPrimarySurfaceBorder = c.brand.surfaceBorder;

export const textColor = c.text.primary;

export const captchaDisplayBorder = c.captcha.border;
export const captchaDisplayBorderWidth = 1.2;
export const captchaDisplayRadius = 8;
export const captchaDisplayBackground = c.captcha.background;

const lightColors = {
	primary: c.brand.primary,
	primaryForeground: c.brand.onPrimary,
	primaryHover: c.brand.primaryHover,
	secondary: c.brand.surface,
	secondaryForeground: c.brand.primaryDark,
	background: c.layout.canvas,
	foreground: c.text.primary,
	surface: c.layout.surface,
	surfaceForeground: c.text.primary,
	surfaceMuted: c.layout.surfaceMuted,
	muted: c.text.muted,
	mutedForeground: c.text.mutedForeground,
	border: c.border.default,
	borderStrong: c.border.strong,
	danger: c.semantic.danger,
	dangerHover: c.semantic.dangerHover,
	dangerForeground: c.semantic.dangerForeground,
	success: c.semantic.success,
	successForeground: c.semantic.successForeground,
	warning: c.semantic.warning,
	warningForeground: c.semantic.warningForeground,
	info: c.semantic.info,
	infoForeground: c.semantic.infoForeground,
	focusRing: c.brand.primary,
	overlay: "rgb(15 23 42 / 0.5)",
	primarySoft: c.brand.surface,
	primarySoftHover: c.brand.surfaceHover,
	primarySoftBorder: c.brand.surfaceBorder,
	primarySoftText: c.brand.primaryDark,
	disabled: c.semantic.disabled,
	disabledBackground: c.semantic.disabledBackground,
} as const;

/** Semantic status pill colors */
export const statusTokens = {
	warningSurface: c.semantic.warningSurface,
	warningText: c.semantic.warningText,
	warningAccent: c.semantic.warningAccent,
	successSurface: c.brand.surface,
	successText: c.semantic.success,
	dangerSurface: c.semantic.dangerSurface,
	dangerText: c.semantic.dangerText,
} as const;

export const chartPaletteTokens = {
	orange: c.chart.orange,
	purple: c.chart.purple,
	teal: c.chart.teal,
} as const;

/** Data table chrome (employer portal pattern) */
export const tableTokens = {
	headerBg: c.table.headerBg,
	headerText: c.table.headerText,
	rowBorder: c.table.rowBorder,
	bodyText: c.table.bodyText,
} as const;

const componentTokens = {
	button: {
		heightSm: "2.25rem",
		heightMd: "2.5rem",
		heightLg: "3.375rem",
		radius: "5px",
		fontWeight: shared.typography.fontWeight.semibold,
	},
	input: {
		height: "3rem",
		radius: "0.625rem",
	},
	focusRingWidth: "2px",
	focusRingOffset: "2px",
} as const;

export const statCardTokens = {
	teal: { accent: c.chart.teal, value: c.text.primary, surface: c.dashboard.statSurface.teal },
	purple: { accent: c.chart.purple, value: c.text.primary, surface: c.dashboard.statSurface.purple },
	coral: { accent: c.chart.orange, value: c.text.primary, surface: c.dashboard.statSurface.coral },
	indigo: { accent: c.chart.indigo, value: c.text.primary, surface: c.dashboard.statSurface.indigo },
} as const;

export type StatCardVariant = keyof typeof statCardTokens;

/** Figma dashboard KPI card border accents (Admin SEPTEMBER design). */
export const dashboardKpiCardTokens = {
	institutes: { border: c.chart.teal, surface: c.dashboard.statSurface.teal },
	jobs: { border: c.chart.orange, surface: c.dashboard.statSurface.coral },
	students: { border: c.chart.purple, surface: c.dashboard.statSurface.purple },
	passout: { border: c.chart.indigo, surface: c.dashboard.statSurface.indigo },
} as const;

/** Pastel dashboard tile backgrounds */
export const dashboardPastelTokens = {
	iceBlue: c.dashboard.pastel.iceBlue,
	mint: c.dashboard.pastel.mint,
	lavender: c.dashboard.pastel.lavender,
	peach: c.dashboard.pastel.peach,
	sky: c.dashboard.pastel.sky,
	lilac: c.dashboard.pastel.lilac,
	rose: c.dashboard.pastel.rose,
	blush: c.dashboard.pastel.blush,
	iconTeal: c.dashboard.icon.teal,
	iconPurple: c.dashboard.icon.purple,
	iconOrange: c.dashboard.icon.orange,
	iconPink: c.dashboard.icon.pink,
} as const;

/** Dashboard chrome — sidebar / header shell (Admin Figma node 19535-122987) */
export const shellTokens = {
	sidebar: {
		headerBackground: c.sidebar.header,
		headerBorder: c.sidebar.border,
		headerBorderWidth: "0.5px",
		primary: c.sidebar.primary,
		foreground: c.sidebar.foreground,
		navActiveBg: c.sidebar.navActive,
		navActiveForeground: c.sidebar.foreground,
		navActiveIcon: c.sidebar.foreground,
		navActiveIndicator: c.sidebar.navActiveIndicator,
		navHoverBg: c.sidebar.navHover,
		border: c.sidebar.border,
		scrollbarThumb: c.sidebar.scrollbarThumb,
		scrollbarTrack: c.sidebar.scrollbarTrack,
		toggleBackground: c.brand.primary,
		toggleHover: c.brand.primaryDark,
		toggleForeground: c.sidebar.foreground,
	},
	header: {
		background: c.layout.header,
		foreground: c.text.header,
		icon: c.brand.primary,
		border: c.border.strong,
	},
} as const;

export type SidebarShellToken = keyof typeof shellTokens.sidebar;
export type HeaderShellToken = keyof typeof shellTokens.header;

export const authAccentTokens = {
	knowledge: c.auth.knowledge,
	skills: c.auth.skills,
	behavior: c.brand.primary,
	heading: c.text.primary,
} as const;

export const themeTokens = {
	shared,
	components: componentTokens,
	statCard: statCardTokens,
	status: statusTokens,
	table: tableTokens,
	shell: shellTokens,
	auth: authAccentTokens,
	light: { colors: lightColors },
} as const;

export type ThemeColorToken = keyof typeof lightColors;
export type ThemeColors = { readonly [K in ThemeColorToken]: string };

export function resolveThemeColors(mode: ThemeMode = "light"): ThemeColors {
	return themeTokens[mode].colors;
}
